// Apolo IDE — EmbedBlockBody (sprint plan-tab-v1, E3).
//
// Iframe sandboxed pra URLs whitelisted (Figma, Notion, Codepen, Tldraw,
// Excalidraw, Miro, Google Slides, Lucid, Canva). Outros viram link com
// preview + abrir externo.

import { useMemo, useState } from 'react';
import { ExternalLink, ShieldAlert } from 'lucide-react';

const EMBED_WHITELIST = [
  { match: /^https?:\/\/(www\.)?figma\.com\//,                        kind: 'figma',     transform: (url) => `https://www.figma.com/embed?embed_host=apolo&url=${encodeURIComponent(url)}` },
  { match: /^https?:\/\/codepen\.io\/.+\/(pen|details|full)\/.+$/i,   kind: 'codepen' },
  { match: /^https?:\/\/(www\.)?notion\.so\//,                        kind: 'notion' },
  { match: /^https?:\/\/(beta\.|www\.)?tldraw\.com\//,                kind: 'tldraw' },
  { match: /^https?:\/\/excalidraw\.com\//,                           kind: 'excalidraw' },
  { match: /^https?:\/\/(www\.)?miro\.com\/app\//,                    kind: 'miro' },
  { match: /^https?:\/\/docs\.google\.com\/presentation\//,           kind: 'gslides' },
  { match: /^https?:\/\/lucid\.app\//,                                kind: 'lucid' },
  { match: /^https?:\/\/(www\.)?canva\.com\/design\//,                kind: 'canva' },
  { match: /^https?:\/\/(www\.)?airtable\.com\/embed\//,              kind: 'airtable' },
];

function classifyUrl(raw) {
  if (!raw) return { ok: false };
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return { ok: false };
    for (const entry of EMBED_WHITELIST) {
      if (entry.match.test(raw)) {
        const final = entry.transform ? entry.transform(raw) : raw;
        return { ok: true, url: final, kind: entry.kind };
      }
    }
    return { ok: false, hostname: u.hostname };
  } catch { return { ok: false }; }
}

export default function EmbedBlockBody({ payload, onChange, accent }) {
  const [url, setUrl] = useState(payload.url || '');
  const classification = useMemo(() => classifyUrl(payload.url || url), [payload.url, url]);

  if (!payload.url) {
    return (
      <div className="flex h-full w-full flex-col gap-2 p-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">URL do embed</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => onChange({ ...payload, url })}
            placeholder="Figma, Codepen, Notion, Tldraw, Miro, Canva…"
            className="h-9 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
          />
        </label>
        <span className="text-[10px] text-apolo-textTertiary">Apenas domínios whitelisted são embedados; outros viram link.</span>
      </div>
    );
  }

  if (!classification.ok) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center">
        <ShieldAlert size={18} className="text-apolo-warning" aria-hidden="true" />
        <span className="text-xs text-apolo-textPrimary">Domínio não whitelisted</span>
        <span className="text-[10px] text-apolo-textTertiary">{classification.hostname || ''}</span>
        <a
          href={payload.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle px-2 py-1 text-[11px] text-apolo-textPrimary hover:bg-apolo-surface2"
        >
          <ExternalLink size={10} /> Abrir no navegador
        </a>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <iframe
        src={classification.url}
        title={`embed: ${classification.kind}`}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
        loading="lazy"
        allowFullScreen
        className="h-full w-full border-0"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-1.5 top-1.5 rounded-full bg-apolo-surface1/80 px-1.5 py-0.5 text-[9px] uppercase tracking-wide backdrop-blur-md"
        style={{ color: accent }}
      >
        {classification.kind}
      </span>
    </div>
  );
}
